import React from 'react';
import { FiShield as Shield, FiEdit2 as Edit, FiTrash2 as Trash2 } from 'react-icons/fi';
import { Card, CardContent } from '../../../components/ui/Card';
import { deleteGuardrailsPolicy } from '../../../services/cloudopsApi';

interface GuardrailPolicy {
  id: string;
  name: string;
  description?: string;
  severity?: string;
  category?: string;
  resource_types?: string[];
}

interface GuardrailCardProps {
  policy: GuardrailPolicy;
  groupNames?: string[];
  onEdit: () => void;
  onDelete?: () => void;
}

const GuardrailCard: React.FC<GuardrailCardProps> = ({ policy, groupNames = [], onEdit, onDelete }) => {
  const getSeverityColor = (severity: string | undefined): string => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'low':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getCategoryColor = (_category: string | undefined): string => {
    return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  };

  const handleDelete = async () => {
    if (!globalThis.confirm(`Are you sure you want to delete the guardrail "${policy.name}"?`)) {
      return;
    }

    try {
      await deleteGuardrailsPolicy(policy.id);
      if (onDelete) onDelete();
    } catch (error: unknown) {
      console.error('Error deleting guardrail:', error);
      alert('Failed to delete guardrail: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(policy.severity)}`}>
                {policy.severity?.toUpperCase() || 'UNKNOWN'}
              </span>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(policy.category)}`}>
                {policy.category?.toUpperCase() || 'UNKNOWN'}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {policy.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {policy.description || 'No description available'}
            </p>
            {groupNames.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {groupNames.map((group) => (
                  <span
                    key={group}
                    className="px-2 py-1 text-xs bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded"
                  >
                    {group}
                  </span>
                ))}
              </div>
            )}
            {(policy.resource_types?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-1">
                {policy.resource_types!.map((type) => (
                  <span
                    key={type}
                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                  >
                    {type}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="ml-4 flex gap-2">
            <button
              onClick={onEdit}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Edit guardrail"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title="Delete guardrail"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default GuardrailCard;

